import type { SimulationConfig, SystemType } from './types.ts';
import { createSimulation } from './simulation.ts';
import { createCanvasRenderer } from './render.ts';
import { createGraphRenderer, type SeriesDef } from './graph.ts';
import { createUI, type UIValues, type UIHandle } from './ui.ts';
import {
  computeEnergies, getAngles, getVelocities,
} from './physics/energies.ts';
import {
  createSpectrumPanel, ChannelRecorder, type SpectrumPanel,
} from './spectrum.ts';
import { createLibrary, type LibraryUI } from './libraryUI.ts';
import { createClosurePanel, type ClosurePanel } from './closure.ts';
import { createClipper, type ClipperHandle, type Marker } from './clipper.ts';
import { createOptimizerPanel, type OptimizerPanel } from './optimizer.ts';
import { DEFAULT_PARAMS } from './types.ts';

function initialStateFromConfig(config: SimulationConfig): number[] {
  const iv = config.initialState;
  return [
    iv.angle1, iv.velocity1, iv.stretch1, iv.stretchRate1,
    iv.angle2, iv.velocity2, iv.stretch2, iv.stretchRate2,
  ];
}

function wrapAngle(a: number): number {
  const PI = Math.PI;
  return a - 2 * PI * Math.floor((a + PI) / (2 * PI));
}

function seriesForSystem(system: SystemType, sculptureN: number): {
  angles: SeriesDef[];
  velocities: SeriesDef[];
  hasElastic: boolean;
} {
  const n = system === 'sculpture' ? Math.max(1, Math.min(4, Math.floor(sculptureN))) : 2;
  const subs = ['\u2080', '\u2081', '\u2082', '\u2083'];
  const angleColors = [
    'rgba(44,70,131,0.9)',
    'rgba(169,130,74,0.95)',
    'rgba(110,58,40,0.9)',
    'rgba(63,95,163,0.9)',
  ];
  const velColors = [
    'rgba(138,102,53,0.9)',
    'rgba(63,95,163,0.9)',
    'rgba(110,58,40,0.9)',
    'rgba(169,130,74,0.95)',
  ];

  const angles: SeriesDef[] = [];
  const velocities: SeriesDef[] = [];
  for (let i = 0; i < n; i++) {
    angles.push({ label: `\u03B8${subs[i]}`, color: angleColors[i] });
    velocities.push({ label: `\u03C9${subs[i]}`, color: velColors[i] });
  }

  return { angles, velocities, hasElastic: system === 'elastic12' || system === 'nonlinear' };
}

interface RecordChannel { label: string; index: number }

function channelsForSystem(system: SystemType, sculptureN: number): RecordChannel[] {
  const sub = ['\u2080', '\u2081', '\u2082', '\u2083'];
  if (system === 'sculpture') {
    const n = Math.max(1, Math.min(4, Math.floor(sculptureN)));
    const out: RecordChannel[] = [];
    for (let i = 0; i < n; i++) {
      out.push({ label: `\u03B8${sub[i]}`, index: 2 * i });
      out.push({ label: `\u03C9${sub[i]}`, index: 2 * i + 1 });
    }
    return out;
  }
  if (system === 'resonantPendulum') {
    return [
      { label: '\u03B8\u2080', index: 0 }, { label: '\u03C9\u2080', index: 1 },
      { label: '\u03B8\u2081', index: 4 }, { label: '\u03C9\u2081', index: 5 },
    ];
  }
  if (system === 'rigid') {
    return [
      { label: '\u03B8\u2081', index: 0 }, { label: '\u03C9\u2081', index: 1 },
      { label: '\u03B8\u2082', index: 4 }, { label: '\u03C9\u2082', index: 5 },
    ];
  }
  return [
    { label: '\u03B8\u2081', index: 0 }, { label: '\u03C9\u2081', index: 1 },
    { label: 'r\u2081', index: 2 }, { label: '\u1E59\u2081', index: 3 },
    { label: '\u03B8\u2082', index: 4 }, { label: '\u03C9\u2082', index: 5 },
    { label: 'r\u2082', index: 6 }, { label: '\u1E59\u2082', index: 7 },
  ];
}

const ENERGY_SERIES: SeriesDef[] = [
  { label: 'KE', color: 'rgba(110,58,40,0.9)' },
  { label: 'PE', color: 'rgba(44,70,131,0.9)' },
  { label: 'EE', color: 'rgba(169,130,74,0.95)' },
];

class App {
  private pendulumCanvas: HTMLCanvasElement;
  private renderer = createCanvasRenderer(
    document.getElementById('pendulumCanvas') as HTMLCanvasElement,
  );

  private energySeries: SeriesDef[];
  private angleSeries: SeriesDef[];
  private velocitySeries: SeriesDef[];
  private hasElastic: boolean;
  private energyGraph: ReturnType<typeof createGraphRenderer>;
  private angleGraph: ReturnType<typeof createGraphRenderer>;
  private velocityGraph: ReturnType<typeof createGraphRenderer>;

  private stepCount = 0;
  private sim: ReturnType<typeof createSimulation> | null = null;
  private config: SimulationConfig | null = null;
  private animId: number | null = null;
  private playing = false;
  private time = 0;
  private statusEl: HTMLElement;

  private channels: RecordChannel[] = [];
  private recorders: ChannelRecorder[] = [];
  private readonly recordCap = 131072;
  private recordScratch: Float64Array = new Float64Array(this.recordCap);
  private spectrum: SpectrumPanel | null = null;
  private liveFrame = 0;
  private ui: UIHandle | null = null;
  private library: LibraryUI | null = null;
  private closure: ClosurePanel | null = null;
  private clipper: ClipperHandle | null = null;
  private keRecorder: ChannelRecorder = new ChannelRecorder(this.recordCap);
  private optimizer: OptimizerPanel | null = null;

  constructor() {
    this.pendulumCanvas = document.getElementById('pendulumCanvas') as HTMLCanvasElement;
    this.statusEl = document.getElementById('status') as HTMLElement;

    this.energySeries = ENERGY_SERIES;
    this.angleSeries = [];
    this.velocitySeries = [];
    this.hasElastic = false;

    this.energyGraph = createGraphRenderer(
      document.getElementById('energyGraph') as HTMLCanvasElement,
      this.energySeries,
    );
    this.angleGraph = createGraphRenderer(
      document.getElementById('angleGraph') as HTMLCanvasElement,
      this.angleSeries,
    );
    this.velocityGraph = createGraphRenderer(
      document.getElementById('velocityGraph') as HTMLCanvasElement,
      this.velocitySeries,
    );

    this.spectrum = createSpectrumPanel({
      channelSelect: document.getElementById('spectrumChannel') as HTMLSelectElement,
      computeBtn: document.getElementById('spectrumComputeBtn') as HTMLButtonElement,
      copyBtn: document.getElementById('spectrumCopyBtn') as HTMLButtonElement,
      liveToggle: document.getElementById('spectrumLive') as HTMLInputElement,
      lookbackSlider: document.getElementById('spectrumLookback') as HTMLInputElement,
      lookbackValue: document.getElementById('spectrumLookbackVal') as HTMLElement,
      canvas: document.getElementById('spectrumCanvas') as HTMLCanvasElement,
      info: document.getElementById('spectrumInfo') as HTMLElement,
      getData: (channel) => this.getChannelData(channel),
      getAllData: () => this.getAllChannelData(),
    });

    this.syncSize();

    this.ui = createUI((values: UIValues) => {
      this.onUIChange(values);
    });

    this.library = createLibrary({
      textarea: document.getElementById('libraryText') as HTMLTextAreaElement,
      loadBtn: document.getElementById('libraryLoad') as HTMLButtonElement,
      exportBtn: document.getElementById('libraryExport') as HTMLButtonElement,
      nameInput: document.getElementById('libraryName') as HTMLInputElement,
      saveBtn: document.getElementById('librarySave') as HTMLButtonElement,
      listEl: document.getElementById('libraryList') as HTMLElement,
      statusEl: document.getElementById('libraryStatus') as HTMLElement,
      getCurrentConfig: () => this.config,
      onLoad: (config) => this.loadConfig(config),
    });

    this.closure = createClosurePanel({
      canvas: document.getElementById('closureCanvas') as HTMLCanvasElement,
      info: document.getElementById('closureInfo') as HTMLElement,
      analyzeBtn: document.getElementById('closureAnalyzeBtn') as HTMLButtonElement,
      liveToggle: document.getElementById('closureLive') as HTMLInputElement,
      getData: () => this.getClosureData(),
    });

    this.syncSize(); // size the closure canvas now that the panel exists

    this.clipper = createClipper({
      getSystem: () => this.config?.system ?? 'rigid',
      getParams: () => this.config?.params ?? DEFAULT_PARAMS,
      getDt: () => this.config?.sim.dt ?? 0.002,
      rangeTrack: this.getById('rangeTrack'),
      rangeThumbL: this.getById('rangeThumbL'),
      rangeThumbR: this.getById('rangeThumbR'),
      rangeHighlight: this.getById('rangeHighlight'),
      rangeMarkers: this.getById('rangeMarkers'),
      rangeTotalLabel: this.getById('rangeTotalLabel'),
      panel: this.getById('clipPanel'),
      canvas: document.getElementById('clipCanvas') as HTMLCanvasElement,
      playBtn: document.getElementById('clipPlayBtn') as HTMLButtonElement,
      speedSlider: document.getElementById('clipSpeed') as HTMLInputElement,
      speedDisplay: this.getById('val_clipSpeed'),
      startDecBtn: document.getElementById('clipStartDec') as HTMLButtonElement,
      startIncBtn: document.getElementById('clipStartInc') as HTMLButtonElement,
      endDecBtn: document.getElementById('clipEndDec') as HTMLButtonElement,
      endIncBtn: document.getElementById('clipEndInc') as HTMLButtonElement,
      startDisplay: this.getById('clipStartVal'),
      endDisplay: this.getById('clipEndVal'),
      frameInfo: this.getById('clipFrameInfo'),
      closeBtn: document.getElementById('clipCloseBtn') as HTMLButtonElement,
      labelFullBtn: document.getElementById('labelFullOrbit') as HTMLButtonElement,
      labelHalfBtn: document.getElementById('labelHalfOrbit') as HTMLButtonElement,
      labelStartBtn: document.getElementById('labelStartOrbit') as HTMLButtonElement,
      onRangeChange: (s, e, markers) => this.updateGraphViews(s, e, markers),
    });

    this.optimizer = createOptimizerPanel({
      methodSelect: document.getElementById('optimizerMethod') as HTMLSelectElement,
      targetSelect: document.getElementById('optimizerTarget') as HTMLSelectElement,
      runBtn: document.getElementById('optimizerRunBtn') as HTMLButtonElement,
      loadBtn: document.getElementById('optimizerLoadBtn') as HTMLButtonElement,
      tempSlider: document.getElementById('optimizerTemp') as HTMLInputElement,
      tempDisplay: this.getById('val_optimizerTemp'),
      coolSlider: document.getElementById('optimizerCool') as HTMLInputElement,
      coolDisplay: this.getById('val_optimizerCool'),
      iterInput: document.getElementById('optimizerIter') as HTMLInputElement,
      perturbSlider: document.getElementById('optimizerPerturb') as HTMLInputElement,
      perturbDisplay: this.getById('val_optimizerPerturb'),
      info: this.getById('optimizerInfo'),
      resultPre: this.getById('optimizerResult'),
      getConfig: () => this.config,
      getMarker: (type) => this.clipper?.getMarkers().find((m) => m.type === type) ?? null,
      getInitialState: () => {
        if (this.clipper) {
          const start = this.clipper.getMarkers().find((m) => m.type === 'start');
          if (start) {
            const sampled = this.clipper.getStateAt(start.frame);
            if (sampled) return sampled;
          }
        }
        return initialStateFromConfig(this.config!);
      },
      onLoad: (state) => this.loadOptimizedState(state),
    });

    window.addEventListener('resize', () => this.syncSize());
  }

  /** Apply an externally-supplied config (e.g. loaded from JSON or the library). */
  private loadConfig(config: SimulationConfig): void {
    if (!this.ui) return;
    this.ui.applyConfigToControls(config);
    this.ui.triggerReset();
  }

  private loadOptimizedState(state: number[]): void {
    if (!this.config || !this.ui) return;
    const newConfig: SimulationConfig = {
      ...this.config,
      initialState: {
        angle1: state[0] ?? 0,
        velocity1: state[1] ?? 0,
        stretch1: state[2] ?? 0,
        stretchRate1: state[3] ?? 0,
        angle2: state[4] ?? 0,
        velocity2: state[5] ?? 0,
        stretch2: state[6] ?? 0,
        stretchRate2: state[7] ?? 0,
      },
    };
    this.ui.applyConfigToControls(newConfig);
    this.ui.triggerReset();
  }

  private getById(id: string): HTMLElement {
    return document.getElementById(id)!;
  }

  private updateGraphViews(startFrame: number, endFrame: number, markers: Marker[]): void {
    const cfg = this.config;
    if (!cfg || this.recorders.length === 0) return;
    const dt = cfg.sim.dt;
    const tStart = startFrame * dt;
    const tEnd = (endFrame + 1) * dt;
    const markerData = markers.map((m) => ({
      time: m.frame * dt,
      type: m.type,
    }));
    this.energyGraph.setViewport(tStart, tEnd);
    this.angleGraph.setViewport(tStart, tEnd);
    this.velocityGraph.setViewport(tStart, tEnd);
    this.energyGraph.setMarkers(markerData);
    this.angleGraph.setMarkers(markerData);
    this.velocityGraph.setMarkers(markerData);
    this.optimizer?.setAvailable(
      markers.some((m) => m.type === 'full'),
      markers.some((m) => m.type === 'half'),
    );
    this.drawFrame();
  }

  private getChannelData(channel: number): { samples: Float64Array; len: number; dt: number } | null {
    const rec = this.recorders[channel];
    const dt = this.config?.sim.dt ?? 0;
    if (!rec || dt <= 0 || rec.length < 64) return null;
    if (rec.length > this.recordScratch.length) {
      this.recordScratch = new Float64Array(rec.length);
    }
    const len = rec.copyInto(this.recordScratch);
    return { samples: this.recordScratch, len, dt };
  }

  private getAllChannelData(): { channels: Float64Array[]; len: number; dt: number } | null {
    const dt = this.config?.sim.dt ?? 0;
    if (dt <= 0 || this.recorders.length === 0) return null;
    const len = this.recorders[0].length;
    if (len < 64) return null;
    const channels: Float64Array[] = this.recorders.map((rec) => {
      const buf = new Float64Array(rec.length);
      rec.copyInto(buf);
      return buf;
    });
    return { channels, len, dt };
  }

  private getClosureData(): { ke: Float64Array; channels: Float64Array[]; len: number; dt: number } | null {
    const dt = this.config?.sim.dt ?? 0;
    if (dt <= 0 || this.recorders.length === 0) return null;
    const len = Math.min(this.keRecorder.length, this.recorders[0].length);
    if (len < 64) return null;
    const ke = new Float64Array(len);
    this.keRecorder.copyInto(ke);
    const channels: Float64Array[] = this.recorders.map((rec) => {
      const buf = new Float64Array(len);
      rec.copyInto(buf);
      return buf;
    });
    return { ke, channels, len, dt };
  }

  private syncSize() {
    const container = this.getById('canvasContainer');
    const rect = container.getBoundingClientRect();
    const cw = Math.floor(rect.width);
    const ch = Math.floor(rect.height);
    this.pendulumCanvas.width = cw;
    this.pendulumCanvas.height = ch;
    this.renderer.resize(cw, ch);

    const gc = this.getById('graphContainer');
    const gw = Math.floor(gc.getBoundingClientRect().width);

    const syncOne = (id: string, h: number, graph: ReturnType<typeof createGraphRenderer>) => {
      const el = document.getElementById(id) as HTMLCanvasElement;
      if (!el) return;
      el.width = gw;
      el.height = h;
      graph.resize(gw, h);
    };

    syncOne('energyGraph', 120, this.energyGraph);
    syncOne('angleGraph', 100, this.angleGraph);
    syncOne('velocityGraph', 100, this.velocityGraph);

    const specCanvas = document.getElementById('spectrumCanvas') as HTMLCanvasElement | null;
    if (specCanvas && this.spectrum) {
      const sh = Math.floor(specCanvas.getBoundingClientRect().height);
      this.spectrum.resize(gw, sh);
    }

    const closeCanvas = document.getElementById('closureCanvas') as HTMLCanvasElement | null;
    if (closeCanvas && this.closure) {
      const ch2 = Math.floor(closeCanvas.getBoundingClientRect().height);
      this.closure.resize(gw, ch2);
    }

    if (!this.playing) this.drawFrame();
    this.clipper?.resize();
  }

  private rebuildGraphs(system: SystemType, sculptureN: number) {
    const s = seriesForSystem(system, sculptureN);
    this.angleSeries = s.angles;
    this.velocitySeries = s.velocities;
    this.hasElastic = s.hasElastic;

    this.channels = channelsForSystem(system, sculptureN);
    this.recorders = this.channels.map(() => new ChannelRecorder(this.recordCap));
    this.keRecorder.clear();
    if (this.spectrum) {
      this.spectrum.setChannels(this.channels.map((c) => c.label));
      this.spectrum.clear();
    }
    if (this.closure) this.closure.clear();
    this.energyGraph = createGraphRenderer(
      document.getElementById('energyGraph') as HTMLCanvasElement,
      this.energySeries,
    );
    this.angleGraph = createGraphRenderer(
      document.getElementById('angleGraph') as HTMLCanvasElement,
      this.angleSeries,
    );
    this.velocityGraph = createGraphRenderer(
      document.getElementById('velocityGraph') as HTMLCanvasElement,
      this.velocitySeries,
    );

    const gc = this.getById('graphContainer');
    const gw = Math.floor(gc.getBoundingClientRect().width);
    this.energyGraph.resize(gw, 120);
    this.angleGraph.resize(gw, 100);
    this.velocityGraph.resize(gw, 100);
  }

  private onUIChange(values: UIValues) {
    const wasPlaying = this.playing;
    const needsReset = !this.config
      || this.config.system !== values.config.system
      || values.reset;

    const needsRebuild = this.config
      && (this.paramsChanged(this.config.params, values.config.params)
        || this.simChanged(this.config.sim, values.config.sim))
      && !needsReset;

    if (values.clearTrail) {
      this.renderer.clearTrail();
      const nodes = this.sim?.getNodes() ?? [{ x: 0, y: 0 }];
      this.renderer.seedTrail(nodes, values.config.system);
      this.stepCount = 0;
    }

    this.config = values.config;
    this.playing = values.playing;

    if (needsReset) {
      this.renderer.clearTrail();
      this.time = 0;
      this.stepCount = 0;
      this.clipper?.clear();
      this.rebuildGraphs(this.config.system, this.config.params.sculptureN);
      const cfg = this.config;
      const state = initialStateFromConfig(cfg);
      this.sim = createSimulation(cfg.system, cfg.params, cfg.sim, state);
      const nodes = this.sim.getNodes();
      this.renderer.seedTrail(nodes, cfg.system);
    } else if (needsRebuild && this.sim) {
      const cfg = this.config;
      const state = this.sim.getState();
      this.sim = createSimulation(cfg.system, cfg.params, cfg.sim, state);
      // Only wipe the recording/spectrum when the channel set itself changes
      // (e.g. sculptureN altered the number of coordinates). For an ordinary
      // parameter drag the channel meaning is unchanged, so keep recording
      // continuously instead of vanishing the analysis on every slider tick.
      const newChannels = channelsForSystem(cfg.system, cfg.params.sculptureN);
      const channelsChanged = newChannels.length !== this.channels.length
        || this.recorders.length !== newChannels.length
        || newChannels.some((c, i) => c.label !== this.channels[i].label);
      this.channels = newChannels;
      if (channelsChanged) {
        this.recorders = newChannels.map(() => new ChannelRecorder(this.recordCap));
        this.keRecorder.clear();
        if (this.spectrum) {
          this.spectrum.setChannels(this.channels.map((c) => c.label));
          this.spectrum.clear();
        }
        if (this.closure) this.closure.clear();
      }
    }

    if (wasPlaying !== this.playing) {
      if (this.playing) this.startLoop();
      else this.stopLoop();
    }

    if (!this.playing) this.drawFrame();
  }

  private paramsChanged(a: import('./types.ts').PendulumParams, b: import('./types.ts').PendulumParams): boolean {
    const keys: (keyof import('./types.ts').PendulumParams)[] = [
      'm1', 'm2', 'L1', 'L2', 'k1', 'k2',
      'sculptureWeight', 'sculptureRod', 'sculptureAxle', 'sculptureReduction', 'sculptureN',
      'rpM0', 'rpM1', 'rpL0', 'rpL1', 'rpA0',
    ];
    for (const k of keys) {
      if (a[k] !== b[k]) return true;
    }
    return false;
  }

  private simChanged(a: import('./types.ts').SimSettings, b: import('./types.ts').SimSettings): boolean {
    return a.dt !== b.dt || a.stepsPerFrame !== b.stepsPerFrame;
  }

  private startLoop() {
    this.stopLoop();
    this.loop();
  }

  private stopLoop() {
    if (this.animId !== null) {
      cancelAnimationFrame(this.animId);
      this.animId = null;
    }
  }

  private loop() {
    if (!this.playing || !this.sim || !this.config) return;
    try {
      const steps = this.config.sim.stepsPerFrame;
      const frameNodes: Array<Array<{ x: number; y: number }>> = [];
      for (let i = 0; i < steps; i++) {
        const result = this.sim.step();
        frameNodes.push(result.nodes);
        this.clipper?.pushFrame(result.nodes, result.state);
        const st = result.state;
        for (let c = 0; c < this.recorders.length; c++) {
          this.recorders[c].push(st[this.channels[c].index]);
        }
        this.keRecorder.push(computeEnergies(this.config.system, this.config.params, st).ke);
        this.time += this.config.sim.dt;
      }
      this.renderer.addSegments(frameNodes, this.config.system);
      this.stepCount += frameNodes.length;
      this.clipper?.flush();

      if (this.spectrum && this.playing) {
        this.liveFrame++;
        if (this.liveFrame % 15 === 0) {
          this.spectrum.refresh();
          if (this.closure) this.closure.refresh();
        }
      }

      const state = this.sim.getState();
      const cfg = this.config;

      const e = computeEnergies(cfg.system, cfg.params, state);
      const angles = getAngles(cfg.system, cfg.params.sculptureN, state).map(wrapAngle);
      const vels = getVelocities(cfg.system, cfg.params.sculptureN, state);

      if (this.hasElastic) {
        this.energyGraph.push([e.ke, e.pe, e.ee], this.time);
      } else {
        this.energyGraph.push([e.ke, e.pe, 0], this.time);
      }
      this.angleGraph.push(angles, this.time);
      this.velocityGraph.push(vels, this.time);

      this.drawFrame();
    } catch (err) {
      // never let a transient error kill the animation loop silently
      console.error('orbit loop error:', err);
    }
    this.animId = requestAnimationFrame(() => this.loop());
  }

  private drawFrame() {
    if (!this.sim || !this.config) return;
    const state = this.sim.getState();
    const nodes = this.sim.getNodes();

    this.renderer.draw({
      currentNodes: nodes,
      state,
      system: this.config.system,
      params: this.config.params,
      diverged: false,
    });

    this.energyGraph.draw();
    this.angleGraph.draw();
    this.velocityGraph.draw();

    this.statusEl.textContent = `t = ${this.time.toFixed(3)}s | trail: ${this.stepCount} pts`;
  }
}

new App();
